import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon
} from '@heroicons/react/20/solid';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  AmsLogStatus,
  AmsNotLoggedReason,
  type OutboundEmailAmsLogFieldsFragment
} from '~/api/graphql';
import { integrationName } from '~/lib/integrations';

import { LineAction } from './artifact-focus-email-ams-line';

/** Why a sent email wasn't logged, finishing "Not logged." */
const NOT_LOGGED_BECAUSE: Record<AmsNotLoggedReason, (amsName: string) => string> = {
  [AmsNotLoggedReason.SeveralMatch]: () => 'Several clients matched.',
  [AmsNotLoggedReason.RecipientsMatchSeveral]: () => 'Several clients matched.',
  [AmsNotLoggedReason.NoMatch]: () => 'No client matched.',
  [AmsNotLoggedReason.Skipped]: () => 'You chose not to log it.',
  [AmsNotLoggedReason.UnsupportedAms]: (amsName) => `${amsName} isn't supported yet.`,
  [AmsNotLoggedReason.UnsupportedConnection]: (amsName) =>
    `${amsName} needs an SDK connection. Ask an admin to switch it in Settings > Integrations.`,
  [AmsNotLoggedReason.SentFromOutlook]: () => 'Sent from Outlook.',
  [AmsNotLoggedReason.LoggingOff]: () => 'Logging was off when it was sent.'
};

const PLAIN_TEXT = 'text-neutral-600 dark:text-neutral-300';

/** The way to push the email from each status it can be pushed from. */
const PUSH_ACTION_LABEL: Partial<Record<AmsLogStatus, (amsName: string) => string>> = {
  [AmsLogStatus.NotLogged]: (amsName) => `Push to ${amsName}`,
  [AmsLogStatus.CouldNotLog]: () => 'Try again',
  [AmsLogStatus.CouldNotConfirm]: () => 'Log again anyway…'
};

/**
 * Where a sent email stands in the AMS, in the composer's status block, with the way to
 * push it when the server says the reader may.
 */
export const SentEmailAmsResult = (props: {
  amsLog: OutboundEmailAmsLogFieldsFragment;
  onPush?: () => void;
}) => {
  const { amsLog } = props;
  const amsName = integrationName(amsLog.integrationType);

  const result = (): React.ReactNode => {
    switch (amsLog.status) {
      case AmsLogStatus.Scheduled:
        return null;
      case AmsLogStatus.Logging:
        return (
          <span className="inline-flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
            <Spinner size="size-3.5" color="neutral" />
            Logging to {amsName}…
          </span>
        );
      case AmsLogStatus.Logged:
        return (
          <>
            <span className="inline-flex items-center gap-1.5 text-neutral-900 dark:text-neutral-100">
              <CheckCircleIcon className="size-4 text-green-600 dark:text-green-400" />
              {amsLog.accountDisplayName ? (
                <span>
                  Logged to <strong className="font-semibold">{amsLog.accountDisplayName}</strong>{' '}
                  in {amsName}.
                </span>
              ) : (
                <span>Logged to {amsName}.</span>
              )}
            </span>
            <span
              title="Logged emails can't be removed or moved from Cara"
              className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400"
            >
              <LockClosedIcon className="size-3" />
              Can&apos;t be undone from Cara
            </span>
          </>
        );
      case AmsLogStatus.NotLogged:
        return (
          <span className={PLAIN_TEXT}>
            Not logged.
            {amsLog.notLoggedReason && ` ${NOT_LOGGED_BECAUSE[amsLog.notLoggedReason](amsName)}`}
          </span>
        );
      case AmsLogStatus.CouldNotLog:
        return (
          <span className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-400">
            <ExclamationTriangleIcon className="size-4" />
            <span>
              Couldn&apos;t log to {amsName}.{amsLog.failureMessage && ` ${amsLog.failureMessage}`}
            </span>
          </span>
        );
      case AmsLogStatus.CouldNotConfirm:
        return (
          <span className="inline-flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
            <ExclamationTriangleIcon className="size-4" />
            Couldn&apos;t confirm it was logged. Check {amsName}.
          </span>
        );
      case AmsLogStatus.Unsupported:
        return (
          <span className="text-neutral-500 dark:text-neutral-400">
            Not logged.{' '}
            {NOT_LOGGED_BECAUSE[amsLog.notLoggedReason ?? AmsNotLoggedReason.UnsupportedAms](
              amsName
            )}
          </span>
        );
    }
  };

  const shown = result();
  if (!shown) return null;
  const pushLabel = PUSH_ACTION_LABEL[amsLog.status]?.(amsName);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm/6">
      {shown}
      {amsLog.canPush && pushLabel && props.onPush && (
        <LineAction onClick={props.onPush}>{pushLabel}</LineAction>
      )}
    </div>
  );
};

/** Every read of a sent email's AMS log failed, so the composer can't say where it stands. */
export const SentEmailAmsUnknown = () => (
  <div className="text-sm/6 text-neutral-500 dark:text-neutral-400">
    Couldn&apos;t check whether this email was logged.
  </div>
);
